import { getSummaryMetrics } from 'js/utils/question-set';

/** Standard question types that trend: only Scored Likert, for now. */
const TRENDED_QUESTION_TYPES = ['likert'];

/**
 * Scored standard questions across a user group's runs. Each question trends on
 * its own, identified by its key, so it follows the question across runs and
 * pages; its name is the text it was last asked with.
 *
 * Returns `questions` (key → { id, name }), `trends` (key → one point per run
 * that asked it, oldest first) and `metrics` (run id → { userGroupAssessment },
 * the runs the chart spans).
 */
export function getStandardQuestionTrends(userGroupAssessments) {
  const liveAssessments = (userGroupAssessments ?? [])
    .filter((uga) => uga.went_live_at && !uga.archived_at && uga.assessment)
    .sort((a, b) => new Date(a.went_live_at) - new Date(b.went_live_at));

  const summaryMetrics = getSummaryMetrics(
    liveAssessments.map((uga) => uga.assessment)
  );

  const questions = {};
  const trends = {};
  const metrics = {};

  liveAssessments.forEach((userGroupAssessment) => {
    const { assessment } = userGroupAssessment;
    const assessmentMetrics = summaryMetrics.assessments[assessment.id];

    if (!assessmentMetrics) {
      return;
    }

    metrics[userGroupAssessment.id] = { userGroupAssessment };

    const questionMetrics = Object.values(
      assessmentMetrics.questionSets ?? {}
    ).reduce((acc, qs) => Object.assign(acc, qs.questions), {});

    (assessment.document?.pages ?? [])
      .filter((page) => page.type === 'standard-questions')
      .flatMap((page) => page.elements ?? [])
      .filter((element) => TRENDED_QUESTION_TYPES.includes(element.type))
      .forEach((element) => {
        questions[element.key] = { id: element.key, name: element.text };
        (trends[element.key] = trends[element.key] ?? []).push({
          userGroupAssessment,
          data: questionMetrics[element.key],
        });
      });
  });

  return { questions, trends, metrics };
}
